import {
  ApiErrorResponse,
  getUserMessageForCode,
  isUnexpectedError,
  type AppErrorOptions,
} from './errors';
import { getPreferredApiLocale } from './locale';

export type RequestInitWithUrl = RequestInit & { url: string };

/**
 * Standart API hata nesnesi; Error'dan türetilir, correlationId ve isUnexpected taşır.
 */
export class AppError extends Error {
  readonly code?: string;
  readonly correlationId?: string;
  readonly isUnexpected: boolean;
  readonly userMessage: string;

  constructor(opts: AppErrorOptions, originalMessage?: string) {
    super(opts.userMessage);
    this.name = 'AppError';
    this.userMessage = opts.userMessage;
    this.code = opts.code;
    this.correlationId = opts.correlationId;
    this.isUnexpected = opts.isUnexpected ?? false;
    if (originalMessage) {
      (this as Error & { originalMessage?: string }).originalMessage = originalMessage;
    }
  }
}

/**
 * response.ok false ise gövdeyi ApiErrorResponse olarak parse eder,
 * code → userMessage eşlemesi yapar ve AppError fırlatır.
 * Parse edilemezse (JSON değil) genel mesaj ve isUnexpected: true kullanılır.
 */
export async function throwOnNotOk(response: Response): Promise<never> {
  const status = response.status;
  let code = 'ERR_INTERNAL';
  let message: string | undefined;
  let correlationId: string | undefined = response.headers.get('X-Correlation-ID') ?? undefined;

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    try {
      const body = (await response.json()) as ApiErrorResponse;
      if (body.code) code = body.code;
      if (body.message) message = body.message;
      if (body.correlationId) correlationId = body.correlationId;
    } catch {
      // JSON parse hatası; code/message varsayılan kalsın
    }
  }

  if (status === 401) {
    setStoredToken(null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
    }
  }

  const userMessage = getUserMessageForCode(code, message);
  const unexpected = isUnexpectedError(code, status);

  throw new AppError({
    userMessage,
    code,
    correlationId,
    isUnexpected: unexpected,
  }, message);
}

const AUTH_TOKEN_KEY = 'buildmate_token';

export function getStoredToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN_KEY);
}

export function setStoredToken(token: string | null): void {
  if (token == null) {
    localStorage.removeItem(AUTH_TOKEN_KEY);
  } else {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
}

/**
 * fetch çağrısı yapar; !res.ok ise throwOnNotOk ile AppError fırlatır.
 * Başarılı yanıtı JSON olarak parse edip döner (body yoksa null).
 * Oturum açıksa Authorization: Bearer <token> eklenir.
 */
export async function apiRequest<T = unknown>(
  url: string,
  init?: RequestInit
): Promise<T> {
  const token = getStoredToken();
  const headers = new Headers(init?.headers);
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  const acceptLanguage = getPreferredApiLocale();
  if (acceptLanguage) {
    headers.set('Accept-Language', acceptLanguage);
  }
  const res = await fetch(url, { ...init, headers });
  if (!res.ok) await throwOnNotOk(res);
  const text = await res.text();
  if (!text.trim()) return null as T;
  return JSON.parse(text) as T;
}
